import { useState, useRef, useEffect } from 'react';
import { isSsr } from './util.js';

let counter = 0;
/**
 * A simple dynamic id value generator for components
 *
 * IMPORTANT: When ever possible, use React 18 `useId` hook instead.
 *
 * This hook should be used only when a component needs an `id` attribute
 * reference for internal purposes and a static `id` property is not given.
 *
 * This implementation guarantees that
 * - id value doesn't change on rerenders
 * - different components receive unique values
 *
 * This implementation doesn't guarantee that the id values remains unchanged
 * on the hydration of a server-side rendered component.
 *
 * @returns string id
 */
function useSimpleId() {
    const [value] = useState(() => counter++);
    // As the React 18 useId hook returns a string that contains a colon
    // character `:`, we include it to the returned string too.
    // See usage note in: https://reactjs.org/docs/hooks-reference.html#useid
    return `:id${value}:`;
}
function useMediaQuery(mediaQuery) {
    // No media query matches on server-side rendering
    if (isSsr()) {
        return false;
    }
    const [matches, setMatches] = useState(
    // Set the initial state in the browser
    window.matchMedia(mediaQuery).matches);
    function handleChange({ matches }) {
        setMatches(matches);
    }
    useEffect(() => {
        const mediaQueryList = window.matchMedia(mediaQuery);
        // Update the state when the media query matches changes
        mediaQueryList.addEventListener('change', handleChange);
        // Update the state when the media query string changes
        handleChange({ matches: mediaQueryList.matches });
        return () => {
            mediaQueryList.removeEventListener('change', handleChange);
        };
    }, [mediaQuery]);
    return matches;
}
function useForwardedRef(ref) {
    const innerRef = useRef(null);
    useEffect(() => {
        if (ref) {
            if (typeof ref === 'function') {
                ref(innerRef.current);
            }
            else {
                ref.current = innerRef.current;
            }
        }
    });
    return innerRef;
}

export { useForwardedRef, useMediaQuery, useSimpleId };
//# sourceMappingURL=hooks.js.map
